import { Inject, Injectable } from '@angular/core';
import { Observable, Subject, takeUntil } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { DBList, TableList, SaveDBDetailsRequestData, ChatResponse } from "@app/shared/models/ask-our-data.model";
import { featureFlags } from '@app/environments/environment';
import { Streamer } from '../streaming/streamer';
import { EventSourceOptions } from '../server-sent-events/sse.client';
import * as XLSX from 'xlsx';

@Injectable({
  providedIn: 'root'
})
export class AskOurDataService {
  private abortStream$ = new Subject<void>();
  protected readonly featureFlags = featureFlags;
  public abortController: AbortController;

  headers = {
    headers: {
      'Content-type': 'application/json'
    }
  };

  constructor(
    @Inject('ASK_DATA_BASE_API_URL') private baseUrl: string,
    private http: HttpClient,
    private streamer: Streamer,
  ) {
    this.abortController = new AbortController();
    this.abortStream$.next();
  }

  getDBList(): Observable<DBList[]> {
    return this.http.get<DBList[]>(`${ this.baseUrl }/v1/database`, this.headers);
  }

  generateQuery(requestData: any): Observable<any> {
    return this.http.post<any>(`${ this.baseUrl }/v1/database/sql/generate`, requestData, this.headers);
  }

  runQuery(runQueryRequestData: { id: string, sql: string, question: string, generate_summary: boolean, session_id?: string | null, chat_id?: string | null, from_chat_interface?: boolean, cap_table_data?: boolean }): Observable<any> {
    let url = `${ this.baseUrl }/v1/database/sql/run`;
   return this.http.post<any>(url, runQueryRequestData, this.headers);
  }

  dbConnect(id: string): Observable<any> {
    return this.http.get<any>(`${ this.baseUrl }/v1/db/connect-id/`+ id, this.headers);
  }

  verifyConnection(requestData: { id?: null | number, type:string, name: string, username: string, server: string, port?:number | null, password?: string | null }): Observable<any> {
    return this.http.post<any>(`${ this.baseUrl }/v1/database/connect`, requestData, this.headers);
  }

  saveDBDetails(requestData: SaveDBDetailsRequestData): Observable<any> {
    return this.http.post<any>(`${ this.baseUrl }/v1/database/configure`, requestData, this.headers);
  }

  updateDBDetails(requestData: SaveDBDetailsRequestData): Observable<any> {
    return this.http.put<any>(`${ this.baseUrl }/v1/database/configure`, requestData, this.headers);
  }

  getTables(id: string): Observable<TableList> {
    return this.http.get<TableList>(`${ this.baseUrl }/v1/database/${id}/tables`, this.headers);
  }

  saveTables(requestData: {"database_id": number | string, "tables_views_list": {"name":string, "is_selected": string, tyep?: string | undefined}[]}): Observable<any> {
     return this.http.post<any>(`${ this.baseUrl }/v1/database/tables`, requestData, this.headers);
  }

  getDDLStatus(id: string): Observable<any> {
   return this.http.get<any[]>(`${ this.baseUrl }/v1/database/${id}/ddl/status`, this.headers);
  }

  getDbDetails(id: string): Observable<any> {
    return this.http.get<any[]>(`${ this.baseUrl }/v1/database/${id}/configure`, this.headers);
  }

  getColumns(id: string): Observable<any> {
    return this.http.get<any[]>(`${ this.baseUrl }/v1/database/${id}/columns`, this.headers);
  }

  saveColumns(requestData: any): Observable<any> {
    return this.http.post<any>(`${ this.baseUrl }/v1/database/columns/train`, requestData, this.headers);
  }

  saveDraftColumnCuration(requestData: any): Observable<any> {
    return this.http.put<any>(`${ this.baseUrl }/v1/database/columns`, requestData, this.headers);
  }

  getColumnStatus(id: string): Observable<any> {
    return this.http.get<any[]>(`${ this.baseUrl }/v1/database/${id}/columns/status`, this.headers);
   }

   setNavigationStatus(requestData: {user_id: string, database_id: number, page_name: string}): Observable<any> {
    return this.http.post<any[]>(`${ this.baseUrl }/v1/database/navigation`, requestData, this.headers);
   }

   getNavigationStatus(user_id: string, database_id: number): Observable<any> {
    return this.http.get<any[]>(`${ this.baseUrl }/v1/database/${database_id}/navigation/${user_id}`, this.headers);
   }

   saveDraftDDL(requestData: any): Observable<any> {
    return this.http.post<any>(`${ this.baseUrl }/v1/database/tables/save`, requestData, this.headers);
   };

   trainSQLPairs(requestData: any): Observable<any> {
    return this.http.post<any>(`${ this.baseUrl }/v1/database/sql/train`, requestData, this.headers);
   }

   generateChart(request: any): Observable<any> {
    return this.http.post<any>(`${ this.baseUrl }/v1/generate_plotly_figure`, request,  this.headers);
   }

   getSQLPairs(id: string): Observable<any> {
    return this.http.get<any[]>(`${ this.baseUrl }/v1/database/${id}/sql`, this.headers);
   }

   saveDraftSQLPairs(requestData: any): Observable<any> {
    return this.http.post<any[]>(`${ this.baseUrl }/v1/database/sql`, requestData, this.headers);
   }

   deleteSQLPair(database_id: number, id: number): Observable<any> {
    const url = `${ this.baseUrl }/v1/database/${database_id}/sql/${id}`;
    return this.http.delete<any[]>(url, this.headers);
   }

   callConversationChat(requestData: any): Observable<any> {
    const url = `${ this.baseUrl }/v1/agent/process`;
    return this.http.post<any[]>(url, requestData, this.headers);
   }

   saveConsent(requestData: any): Observable<any> {
    const url = `${ this.baseUrl }/v1/database/chat/acknowledge`;
    return this.http.post<any[]>(url, requestData, this.headers);
   }

   getSamplePrompts(database_id: number, number_of_prompts: number): Observable<any> {
    const url = `${ this.baseUrl }/v1/database/chat/${database_id}/prompt/${number_of_prompts}`;
    return this.http.get<any[]>(url, this.headers);
   }

   advancePlot(requestData: any): Observable<any> {
    const url = `${ this.baseUrl }/v1/advance_plot`;
    return this.http.post<any[]>(url,requestData,  this.headers);
   }

   advancePrediction(requestData: any): Observable<any> {
    const url = `${ this.baseUrl }/v1/advance_prediction`;
    return this.http.post<any[]>(url, requestData, this.headers);
   }

   getChatHistoryList(databaseId: any, userId: any): Observable<any> {
    const url = `${ this.baseUrl }/v1/database/${databaseId}/user/${userId}/chat/titles`;
    return this.http.get<any[]>(url, this.headers);
   }

   deleteChat(databaseId: number | string, userId: number | string, sessionId: string): Observable<any> {
    const url = `${ this.baseUrl }/v1/database/${databaseId}/user/${userId}/session/${sessionId}/delete`;
    return this.http.delete<any[]>(url, this.headers);
   }

   renameChatTitle(requestData: any): Observable<any> {
    const url = `${ this.baseUrl }/v1/database/${requestData.databaseId}/user/${requestData.userId}/chat/${requestData.chatId}/title`;
    return this.http.put<any[]>(url, { new_chat_title: requestData.new_chat_title }, this.headers);
   }

   getChatDetails(databaseId: number | string, userId: number | string, sessionId: string): Observable<any> {
    const url = `${ this.baseUrl }/v1/database/${databaseId}/user/${userId}/session/${sessionId}/chat`;
    return this.http.get<any[]>(url, this.headers);
   }

   codeInterpreterPlot(requestData: any): Observable<any> {
    const url = `${ this.baseUrl }/v1/code_interpreter_plot`;
    return this.http.post<any[]>(url, requestData, this.headers);
   }

   codeInterpreterPlotly(requestData: any): Observable<any> {
    const url = `${ this.baseUrl }/v1/code_interpreter_plotly`;
    return this.http.post<any[]>(url, requestData, this.headers);
   }

   saveChatHistory(requestData: any): Observable<any> {
    const url = `${ this.baseUrl }/v1/chats`;
    requestData.source = 'AOData_API';
    return this.http.post<any[]>(url, requestData, this.headers);
   }

  fetchResponse(requestData: any): Observable<any> {
    requestData.source = 'AOData_API';

    const props: EventSourceOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      payload: JSON.stringify(requestData),
    };
    const url = `${this.baseUrl}/v1/prompt`;

    return this.streamer.stream<ChatResponse>(url, props)
      .pipe(
        takeUntil(this.abortStream$)
      );
  }

  abortRequest() {
    if (this.abortController) {
      this.abortController.abort();
    }
  }

   getDocumentCurationData(databaseId: string): Observable<any> {
    const url = `${ this.baseUrl }/v1/database/${databaseId}/document`;
    return this.http.get<any[]>(url, this.headers);
   }

   getDatabaseSchemas(requestData: { type: string, server: string, port?: number | null, name: string, username: string, password?: string | null }): Observable<any> {
    const url = `${ this.baseUrl }/v1/database/schema`;
    return this.http.post<any[]>(url, requestData, this.headers);
   }

   setDocumentCurationData(requestData: any): Observable<any> {
    const url = `${ this.baseUrl }/v1/database/document`;
    return this.http.post<any[]>(url, requestData, this.headers);
   }

   trainDocumentCurationData(requestData: any): Observable<any> {
    const url = `${ this.baseUrl }/v1/database/${requestData.database_id}/document/train`;
    return this.http.post<any[]>(url, requestData, this.headers);
   }

   saveFeedback(requestData: any): Observable<any> {
    const url = `${ this.baseUrl }/v1/feedback`;
    return this.http.post<any[]>(url, requestData, this.headers);
   }

   getChart(requestData: any): Observable<any> {
    const url = `${ this.baseUrl }/v1/chat-history/plot`;
    return this.http.put<any[]>(url, requestData, this.headers);
   }

   exportSQLPairs(databaseId: string): Observable<any> {
    const url = `${ this.baseUrl }/v1/database/${databaseId}/sql/export`;
    return this.http.get(url, { ...this.headers, responseType: 'blob' });
  }

   downloadColumnCurationData(requestData: any): Observable<Blob> {
    const headers = {
      'Content-type': 'application/json'
    };
    const url = `${ this.baseUrl }/v1/database/${requestData.database_id}/columns/download`;
    return this.http.post<Blob>(url, requestData, { headers, responseType: 'blob' as 'json'  });
   }

   uploadColumnCurationFiles(file: File, databaseId: string): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    const url = `${ this.baseUrl }/v1/database/${databaseId}/columns/import`;
    return this.http.put<any>(url, formData, {
      headers: { 'enctype': 'multipart/form-data' },
      reportProgress: true,
      observe: 'events'
    });
  }

  uploadSqlCurationFiles(file: File, databaseId: string): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    const url = `${ this.baseUrl }/v1/database/${databaseId}/sql/import`;

     return this.http.post<any>(url, formData, {
       headers: { 'enctype': 'multipart/form-data' },
       reportProgress: true,
       observe: 'events'
     });
  }
  
  exportFullTableDataAsExcel(data: any[], promptValue: string, fileName: string): void {
    const header = ['Sl no', ...Object.keys(data[0] || {})];
    const tableRows = data.map((row, idx) => [idx + 1, ...header.slice(1).map(h => row[h])]);

    const worksheetData = [
      [ "Response to: " + promptValue, ...Array(header.length - 1).fill("") ],
      [],
      header,
      ...tableRows
    ];

    const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(worksheetData);

    if (header.length > 1) {
      ws['!merges'] = ws['!merges'] || [];
      ws['!merges'].push({
        s: { r: 0, c: 0 },
        e: { r: 0, c: header.length - 1 }
      });
    }

    const wb: XLSX.WorkBook = { Sheets: { 'data': ws }, SheetNames: ['data'] };
    XLSX.writeFile(wb, `${fileName}.xlsx`);
  }

  downloadFeedback(requestData: any): Observable<Blob> {
    const headers = {
      'Content-type': 'application/json'
    };
    const url = `${ this.baseUrl }/v1/database/${requestData.database_id}/feedback/export?start_date=${requestData.start_date}&end_date=${requestData.end_date}`;
    return this.http.get<Blob>(url, { headers, responseType: 'blob' as 'json'  });
  }

  getDBOwnerSettings(databaseId: string): Observable<any> {
    const url = `${ this.baseUrl }/v1/database/${databaseId}/dbowner/settings`;
    return this.http.get<any[]>(url, this.headers);
  } 

  editSqlPairTitle(requestData: any): Observable<any> {
    const url = `${ this.baseUrl }/v1/database/sql/title`;
    return this.http.post<any>(url, requestData, this.headers);
  }

  startAutoCuration(requestData: any): Observable<any> {
    const url = `${ this.baseUrl }/v1/database/curation`;
    return this.http.post<any>(url, requestData, this.headers);
  }

  getCurationStatus(databaseId: string): Observable<any> {
    const url = `${ this.baseUrl }/v1/database/${databaseId}/curation/status`;
    return this.http.get<any[]>(url, this.headers);
  }

  refreshInstructionsFormData(databaseId: string): Observable<any> {
    const url = `${ this.baseUrl }/v1/database/${databaseId}/document/generate`;
    return this.http.get<any[]>(url, this.headers);
  }
  
}